import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { useLanguage } from '@/hooks/use-language';
import { useToast } from '@/hooks/use-toast';
import { getGroupColorClasses, getLocalizedGroupName } from '@/utils/groupColors';
import { cn } from '@/lib/utils';

interface CreateMemberDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreateMember: (member: {
    name: string;
    phone: string;
    email: string;
    groups: string[];
    subgroups: string[];
    isAdmin: boolean;
  }) => void;
}

const availableGroups = ['Main Padel Tennis Group'];
const availableSubgroups = [
  'Nybegynner / Beginner',
  'Middels / Intermediate', 
  'Middels Pluss / Intermediate Plus',
  'Avansert / Advanced'
];

export function CreateMemberDialog({ open, onOpenChange, onCreateMember }: CreateMemberDialogProps) {
  const { t, language } = useLanguage();
  const { toast } = useToast();

  const [formData, setFormData] = React.useState({
    name: '',
    phone: '',
    email: '',
    groups: [] as string[],
    subgroups: [] as string[],
    isAdmin: false
  });

  const [errors, setErrors] = React.useState<Record<string, string>>({});

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = t("validation.required");
    }

    if (!formData.phone.trim()) {
      newErrors.phone = t("validation.required");
    } else if (!/^\+?[\d\s-]+$/.test(formData.phone)) {
      newErrors.phone = t("validation.phoneFormat");
    }

    if (!formData.email.trim()) {
      newErrors.email = t("validation.required");
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t("validation.emailFormat");
    }

    if (formData.groups.length === 0) {
      newErrors.groups = t("validation.selectGroup");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      onCreateMember(formData);
      handleReset();
      onOpenChange(false);
      toast({
        title: t("members.memberCreated"),
        description: t("members.memberCreatedDesc", { name: formData.name })
      });
    }
  };

  const handleReset = () => {
    setFormData({
      name: '',
      phone: '',
      email: '',
      groups: [],
      subgroups: [],
      isAdmin: false
    });
    setErrors({});
  };

  const handleCancel = () => {
    handleReset();
    onOpenChange(false);
  };

  const handleGroupToggle = (group: string) => {
    setFormData(prev => ({
      ...prev,
      groups: prev.groups.includes(group)
        ? prev.groups.filter(g => g !== group)
        : [...prev.groups, group]
    }));
  };

  const handleSubgroupToggle = (subgroup: string) => {
    setFormData(prev => ({
      ...prev,
      subgroups: prev.subgroups.includes(subgroup)
        ? prev.subgroups.filter(s => s !== subgroup)
        : [...prev.subgroups, subgroup]
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("members.createMember")}</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="font-medium text-sm text-muted-foreground">{t("members.basicInfo")}</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">{t("members.name")} *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder={t("members.enterName")}
                />
                {errors.name && <p className="text-sm text-destructive">{errors.name}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">{t("members.phone")} *</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder={t("placeholder.phoneNumber")}
                />
                {errors.phone && <p className="text-sm text-destructive">{errors.phone}</p>}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">{t("members.email")} *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                placeholder={t("placeholder.email")}
              />
              {errors.email && <p className="text-sm text-destructive">{errors.email}</p>}
            </div>
          </div>

          {/* Admin Privileges */}
          <div className="space-y-4">
            <h3 className="font-medium text-sm text-muted-foreground">{t("members.roleAndPermissions")}</h3>
            
            <div
              className="flex items-center space-x-2 cursor-pointer p-2 rounded hover:bg-muted/50 -mx-2"
              onClick={() => setFormData(prev => ({ ...prev, isAdmin: !prev.isAdmin }))}
            >
              <Checkbox
                id="isAdmin"
                checked={formData.isAdmin}
                onChange={() => {}} // Controlled by parent div
              />
              <Label htmlFor="isAdmin" className="cursor-pointer flex-1">{t("members.adminPrivileges")}</Label>
            </div>
          </div>

          {/* Group Selection */}
          <div className="space-y-4">
            <h3 className="font-medium text-sm text-muted-foreground">{t("members.groupMembership")}</h3>
            
            <div className="space-y-2">
              <Label>{t("members.groups")} *</Label>
              <div className="space-y-2">
                {availableGroups.map(group => {
                  const colors = getGroupColorClasses(group);
                  return (
                    <div 
                      key={group} 
                      className="flex items-center space-x-2 cursor-pointer p-2 rounded hover:bg-muted/50 -mx-2"
                      onClick={() => handleGroupToggle(group)}
                    >
                      <Checkbox
                        checked={formData.groups.includes(group)}
                        onChange={() => {}} // Controlled by parent div
                      />
                      <Badge 
                        variant="outline" 
                        className={cn("text-xs border-transparent", colors.bg, colors.text)}
                      >
                        {getLocalizedGroupName(group, language)}
                      </Badge>
                    </div>
                  );
                })}
              </div>
              {errors.groups && <p className="text-sm text-destructive">{errors.groups}</p>}
            </div>

            <div className="space-y-2">
              <Label>{t("members.subgroups")}</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {availableSubgroups.map(subgroup => {
                  const colors = getGroupColorClasses(subgroup);
                  return (
                    <div 
                      key={subgroup} 
                      className="flex items-center space-x-2 cursor-pointer p-2 rounded hover:bg-muted/50 -mx-2"
                      onClick={() => handleSubgroupToggle(subgroup)}
                    >
                      <Checkbox
                        checked={formData.subgroups.includes(subgroup)}
                        onChange={() => {}} // Controlled by parent div
                      />
                      <Badge 
                        variant="outline" 
                        className="text-xs"
                        style={{
                          borderColor: `hsl(var(--group-${colors.variant}))`,
                          color: `hsl(var(--group-${colors.variant}))`
                        }}
                      >
                        {getLocalizedGroupName(subgroup, language)}
                      </Badge>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-2 pt-4 border-t">
          <Button variant="outline" onClick={handleCancel}>
            {t("common.cancel")}
          </Button>
          <Button onClick={handleSubmit}>
            {t("members.createMember")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
